<template>
  <div>
    <div class="d-flex flex-wrap align-center mb-4" style="gap: 12px">
      <v-text-field v-model="search" label="Поиск по ФИО или email" append-icon="mdi-magnify" outlined dense hide-details clearable style="max-width: 300px" />
    </div>

    <v-alert v-if="addManagerHint" type="info" outlined dense class="mb-4">
      Менеджера добавляет сам корп. клиент в своём кабинете. Добавление менеджера администратором появится после доработки бэка.
    </v-alert>

    <StateBlock v-if="loadingManagers && !rows.length" state="loading" />
    <StateBlock v-else-if="loadError" state="error" @retry="loadManagers(true)" />
    <StateBlock v-else-if="!rows.length && !search" state="empty" title="Менеджеров пока нет"
      text="Менеджеры нужны, если тесты распределяются по городам, филиалам или сотрудникам." />

    <v-data-table
      v-else
      :headers="headers"
      :items="rows"
      :loading="loadingManagers"
      item-key="id"
      show-expand
      single-expand
      disable-pagination
      hide-default-footer
      no-data-text="Менеджеров не найдено"
    >
      <template v-slot:item.name="{ item }">
        <router-link v-if="$store.getters['auth/isAllowed']('view_manager')" class="text-primary" :to="{ name: 'managers-id', params: { id: item.id } }">
          {{ item.name }}
        </router-link>
        <span v-else>{{ item.name }}</span>
      </template>
      <template v-slot:item.balance="{ item }">
        <span :class="{ 'error--text': item.balance < 0 }">{{ item.balance }}</span>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-tooltip v-if="canDistribute" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small v-bind="attrs" v-on="on" :disabled="!wallet || !wallet.bufferBalance" @click="openDistribute(item.id)">
              <v-icon small>mdi-swap-horizontal</v-icon>
            </v-btn>
          </template>
          <span>Распределить этому менеджеру</span>
        </v-tooltip>
      </template>
      <template v-slot:expanded-item="{ headers: cols, item }">
        <td :colspan="cols.length" class="py-3">
          <div v-if="!item.tests.length" class="caption grey--text">Тестов нет</div>
          <v-simple-table v-else dense style="max-width: 520px">
            <thead><tr><th>Тест</th><th class="text-right">Остаток</th><th class="text-right">Получено*</th><th class="text-right">Израсходовано*</th></tr></thead>
            <tbody>
              <tr v-for="t in item.tests" :key="t.id">
                <td>{{ t.name }}</td><td class="text-right">{{ t.balance }}</td><td class="text-right">{{ t.received }}</td><td class="text-right">{{ t.spent }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <div class="caption grey--text text--darken-1 mt-1">* за период, выбранный на вкладке «Обзор»</div>
        </td>
      </template>
    </v-data-table>

    <WalletTransferDialog
      v-model="distribute.show"
      mode="distribute"
      :corp-id="corpId"
      :items="wallet ? wallet.items : []"
      :managers="wallet ? wallet.managers : []"
      :preset-manager="distribute.manager"
      @success="$emit('reload')"
    />
    <ManagerCreateModal v-if="canAddManager" />

    <v-dialog v-model="distributePrompt.show" max-width="460">
      <v-card class="pa-6">
        <h3 class="font-md mb-2">Распределить услуги этому менеджеру?</h3>
        <div class="grey--text text--darken-1 mb-5">
          {{ distributePrompt.name }} добавлен. Можно сразу передать ему тесты из буфера компании или сделать это позже.
        </div>
        <div class="d-flex justify-end" style="gap: 12px">
          <v-btn text @click="distributePrompt.show = false">Не сейчас</v-btn>
          <v-btn color="primary" @click="startDistributeToNew">Распределить</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import api from '@/common/api';
import StateBlock from '@/components/ui/StateBlock.vue';
import ManagerCreateModal from '@/components/managers/ManagerCreateModal.vue';
import WalletTransferDialog from './WalletTransferDialog.vue';

// Менеджеры корпа: список и контакты — из /users/managers/?corp_client=ID (служебного менеджера сервер скрывает),
// остатки — из сводки кошелька. Список кэшируем ненадолго отдельно для каждого корпа.
const MANAGERS_CACHE_MS = 60 * 1000;
const managersCache = new Map();
export default {
  name: 'ClientManagers',
  components: { StateBlock, ManagerCreateModal, WalletTransferDialog },
  props: {
    corpId: { type: [Number, String], required: true },
    wallet: { type: Object, default: null },
    canAddManager: { type: Boolean, default: false },
    canDistribute: { type: Boolean, default: false },
    // Пришли из шага «Добавить менеджера?» после создания корпа
    openAddOnStart: { type: Boolean, default: false },
  },
  data: () => ({
    search: '',
    // Свой список: общий стор users/managers занят страницей «Менеджеры» (фильтры, пагинация).
    managers: [],
    loadingManagers: false,
    loadError: false,
    addManagerHint: false,
    distribute: { show: false, manager: null },
    distributePrompt: { show: false, managerId: null, name: '' },
  }),
  computed: {
    ...mapGetters({
      dialogCreateShow: 'users/managers/dialogCreateShow',
    }),
    rows() {
      const walletById = new Map((this.wallet ? this.wallet.managers : []).map((m) => [String(m.id), m]));
      // Сервер уже отфильтровал по корпу; customer приходит в каждой записи — страхуемся от чужих менеджеров.
      const own = this.managers.filter((m) => !m.is_auxiliary
        && m.customer && String(m.customer.id) === String(this.corpId));
      const list = own.map((m) => {
        const w = walletById.get(String(m.id));
        return {
          id: m.id,
          name: [m.last_name, m.first_name].filter(Boolean).join(' ') || 'Имя не задано',
          email: m.email || '—',
          candidates: m.candidates_count != null ? m.candidates_count : '—',
          balance: w ? w.balance : 0,
          tests: w ? w.tests : [],
        };
      });
      // Менеджеры, которые есть в сводке, но не пришли в общем списке (например, нет права на список) — показываем по сводке.
      walletById.forEach((w, id) => {
        if (!list.some((m) => String(m.id) === id)) {
          list.push({
            id: w.id, name: w.name, email: '—', candidates: '—', balance: w.balance, tests: w.tests,
          });
        }
      });
      const q = (this.search || '').trim().toLowerCase();
      return list
        .filter((m) => !q || m.name.toLowerCase().includes(q) || String(m.email).toLowerCase().includes(q))
        .sort((a, b) => b.balance - a.balance);
    },
    headers() {
      return [
        { text: 'ФИО', value: 'name' },
        { text: 'Email', value: 'email' },
        { text: 'Остаток тестов', value: 'balance', align: 'end' },
        { text: 'Кандидатов', value: 'candidates', align: 'end' },
        {
 text: '', value: 'actions', align: 'end', sortable: false, width: '60px'
},
        { text: '', value: 'data-table-expand' },
      ];
    },
  },
  watch: {
    // Переход между карточками корпов без пересоздания компонента
    corpId() {
      this.managers = [];
      this.loadManagers();
    },
    // После создания менеджера обновляем список и остатки
    async dialogCreateShow(val, old) {
      if (!(old && !val)) return;
      const knownIds = new Set(this.managers.map((m) => String(m.id)));
      await this.loadManagers(true);
      // Новый менеджер появился — обновляем остатки; корп может сразу распределить ему услуги (П-2)
      const created = this.managers.find((m) => !m.is_auxiliary && !knownIds.has(String(m.id)));
      if (created) this.$emit('reload');
      if (created && this.canDistribute && this.wallet && this.wallet.bufferBalance > 0) {
        const name = [created.last_name, created.first_name].filter(Boolean).join(' ') || 'Менеджер';
        this.distributePrompt = { show: true, managerId: created.id, name };
      }
    },
  },
  methods: {
    ...mapActions({
      setDialogCreateShow: 'users/managers/setDialogCreateShow',
    }),
    async loadManagers(force = false) {
      this.loadError = false;
      const cacheKey = String(this.corpId);
      const cached = managersCache.get(cacheKey);
      if (!force && cached && Date.now() - cached.at < MANAGERS_CACHE_MS) {
        this.managers = cached.list;
        return;
      }
      this.loadingManagers = true;
      try {
        const response = await api.get('/users/managers/', { params: { corp_client: this.corpId, page_size: -1 } });
        this.managers = Array.isArray(response) ? response : (response.results || []);
        managersCache.set(cacheKey, { at: Date.now(), list: this.managers });
      } catch (e) {
        this.loadError = true;
      } finally {
        this.loadingManagers = false;
      }
    },
    openCreate() {
      this.setDialogCreateShow(true);
    },
    openDistribute(managerId) {
      this.distribute = { show: true, manager: managerId };
    },
    startDistributeToNew() {
      this.distributePrompt.show = false;
      this.openDistribute(this.distributePrompt.managerId);
    },
  },
  created() {
    this.loadManagers();
    if (this.openAddOnStart) {
      if (this.canAddManager) this.openCreate();
      else this.addManagerHint = true;
    }
  },
};
</script>
